/**
 * Navigation and instance-rendering components for routes that feature a
 * single structure instance in the main content of the view.
 *
 * `DatastoreProvider` mounts the `Datastore` for a route from a resolved
 * `InstanceConfig` (`data/instance-config`), and each route renders its screen
 * directly inside the provider.
 * The navigators (`InstanceStack`, `InstanceDashboard`,
 * `InstanceDashboardStack`) provide the surrounding chrome — headers,
 * dashboards, focus containment, bottom-overlay widgets — that these screens
 * render within.
 */

import { useQueryClient } from '@tanstack/react-query';
import { RestrictedScreen } from 'component/admin';
import { spacings } from 'component/constants';
import type { DashboardConfig, DashboardTab, IsTabAllowedProps } from 'component/dashboard';
import { Forbidden } from 'component/errors/forbidden';
import { ConfirmLeaveModal } from 'component/modal/general/confirm-leave';
import { LoadingSpinner, LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useTranslate } from 'component/translation';
import { Breadcrumb, Breadcrumbs } from 'component/ui/breadcrumbs';
import { useSettings } from 'data/settings';
import { Redirect, Stack, withLayoutContext } from 'expo-router';
import {
    getFocusedRouteNameFromRoute,
    type NavigationAction,
    type ParamListBase,
    type RouteProp,
    type TabNavigationState,
    type Theme,
    useNavigation,
    usePreventRemove,
} from 'expo-router/react-navigation';
import { FocusLoopView } from 'platform-specific/focustrap';
import { KeyboardShortcutHandler } from 'platform-specific/keyboardShortcutHandler';
import { OverlayPortalBoundary } from 'platform-specific/overlay-portal';
import React, { useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useApiClient, useApiQueryUtils } from 'system/api-client-context';
import { Catcher } from 'system/catcher';
import { useSiloLanguage } from 'system/request-metadata';
import { useMyRoles } from 'util/admin';
import { useAppData } from 'util/appdataContext';
import { Datastore, useDatastore, useIsInSidebar, useSessionData } from 'util/datastore';
import type { InstanceConfig } from 'util/features';
import { useConfig } from 'util/features-hooks';
import { LAYOUT_BREAKPOINT_LARGE } from 'util/layout';
import { closeSidebar } from 'util/navigate';
import { initializeGlobalSidebarTimers } from 'util/timers';

import { createDashboardNavigator, type DashboardNavigationEventMap, type DashboardNavigationOptions } from './dashboard';
import { NavigationHeader, NavigationHeaderActionProvider } from './navigation-header';
import { breadcrumbLinkRenderer, NestedStackBreadcrumbsLayout, useBuildNestedRouteHref } from './nested-stack';

const DashboardNavigator = createDashboardNavigator().Navigator;

/**
 * A dashboard navigator integrated with Expo Router context.
 *
 * Unlike the standard Expo Router navigators, this one passes `true` for the
 * third argument of `withLayoutContext` so that it renders only user-defined
 * screens so that the available tabs can be set explicitly.
 */
const DashboardNavigatorWithContext = withLayoutContext<
    DashboardNavigationOptions,
    typeof DashboardNavigator,
    TabNavigationState<ParamListBase>,
    DashboardNavigationEventMap
>(DashboardNavigator, undefined, true);

/**
 * `loadingScreen` shows until the allowed tabs resolve. It defaults to a
 * spinner.
 */
type InstanceDashboardProps = Omit<React.ComponentProps<typeof DashboardNavigatorWithContext>, 'children' | 'title'> & {
    loadingScreen?: React.ReactNode;
};

/**
 * A dashboard navigator for the instance in context.
 *
 * The instance should provide a `DashboardConfig` to configure the dashboard.
 *
 * @public
 */
export function InstanceDashboard(props: InstanceDashboardProps) {
    const { dashboardCapability, dashboardTabs, dashboardTitleLabel } = useConfig<DashboardConfig>();
    const translate = useTranslate();
    const title = translate(dashboardTitleLabel);
    const allowedTabs = useAllowedDashboardTabs(dashboardTabs);
    const buildTabHref = useBuildNestedRouteHref();
    const { backBehavior = 'none', layout, screenOptions, loadingScreen = <LoadingSpinner />, ...rest } = props;

    if (!allowedTabs) {
        return loadingScreen;
    }

    return (
        <RestrictedScreen capability={dashboardCapability}>
            <DashboardNavigatorWithContext
                {...rest}
                backBehavior={backBehavior}
                // eslint-disable-next-line react/no-unstable-nested-components
                layout={layout ?? ((props) => <InstanceLayout {...props} />)}
                screenOptions={(props) => ({
                    // eslint-disable-next-line react/no-unstable-nested-components
                    header: (props) => <NavigationHeader {...props} />,
                    headerLeft: () => null,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    headerTitle: ({ children }) => (
                        <Breadcrumbs>
                            <Breadcrumb href={buildTabHref(props.route)} render={breadcrumbLinkRenderer()}>
                                {children}
                            </Breadcrumb>
                        </Breadcrumbs>
                    ),
                    ...resolveScreenOptions(screenOptions, props),
                })}
                title={title}
            >
                {allowedTabs.map((tab) => {
                    const title = tab.label ? translate(tab.label) : tab.text;
                    return (
                        <DashboardNavigatorWithContext.Screen
                            key={tab.key}
                            name={tab.key}
                            options={({ route }) => ({
                                tabBarButtonTestID: tab.testID,
                                // eslint-disable-next-line react/no-unstable-nested-components
                                tabBarIcon: ({ color }) => <tab.icon color={color as string} size={20} />,
                                tabBarLabel: title,
                                tabBarVisible: isTabBarVisible(route, tab),
                                title,
                            })}
                        />
                    );
                })}
            </DashboardNavigatorWithContext>
        </RestrictedScreen>
    );
}

/**
 * Redirect to the first allowed tab of a dashboard.
 *
 * Render this component as an index route for dashboards.
 *
 * @privateRemarks
 * Without an index route, the tab name that is first in alphabetical order will
 * display in the URL during navigation to a dashboard. This behavior may be an
 * artifact of the way the configuration resolves asynchronously. After changing
 * instance layouts to suspend while configuration resolves, check that this is
 * still necessary.
 *
 * @public
 */
export function InstanceDashboardRedirect() {
    const { dashboardTabs } = useConfig<DashboardConfig>();
    const allowedTabs = useAllowedDashboardTabs(dashboardTabs);

    if (!allowedTabs) return null;
    const tab = allowedTabs[0];
    if (tab) {
        return <Redirect href={`./${tab.key}`} relativeToDirectory />;
    }

    return <Forbidden />;
}

/**
 * A stack navigator for instance screens nested inside a dashboard tab.
 *
 * @public
 */
export function InstanceDashboardStack(props: React.ComponentProps<typeof Stack>) {
    const { layout, screenOptions, ...rest } = props;
    return (
        <InstanceStack
            {...rest}
            // eslint-disable-next-line react/no-unstable-nested-components
            layout={layout ?? ((props) => <NestedStackBreadcrumbsLayout {...props} />)}
            screenOptions={(props) => ({
                headerShown: false,
                ...resolveScreenOptions(screenOptions, props),
            })}
        />
    );
}

export type InstanceStackProps = React.ComponentProps<typeof Stack>;

/**
 * The screen stack for a PSI instance: an expo-router Stack wired up with the
 * shared navigation header and, when embedded as a host panel, a focus trap
 * whose Escape closes the panel — making the whole stack behave like a modal
 * in the host page.
 *
 * The focus trap is gated on the rendering environment, not the structure.
 * It mounts wherever PSI renders as a panel the host dismisses — the sidebar
 * iframe and the native-app webview — and there Tab is contained and Escape
 * closes the panel through the FocusLoopView's onCloseLoop. At top level
 * there is no host panel to close, and a full-page trap would be a WCAG
 * 2.1.2 keyboard trap, so none is mounted.
 *
 * No autoFocus: the iframe can load before the sidebar opens, and eagerly
 * focusing would show an unwanted focus ring (WCAG 2.4.7).
 *
 * Inside the trap, react-aria overlays (popovers, menus, tooltips) are
 * re-portaled into the trapped subtree via OverlayPortalBoundary. Without it
 * they portal to document.body — outside the trap's DOM — and the trap's
 * focus containment steals focus back on open, flickering them shut.
 *
 * The tabIndex={-1} wrapper around the trapped content gives clicks on
 * non-focusable areas (whitespace, plain text) somewhere to land other than
 * document.body. Without it, such a click blurs the active element to
 * nowhere, and react-aria's FocusScope restores focus via a bare .focus()
 * call that scrolls the page back to wherever that element is — jarring if
 * the user had scrolled it out of view first. Landing the click on this
 * wrapper instead keeps focus inside the scope, so the restore never fires.
 */
export function InstanceStack(props: InstanceStackProps) {
    const { layout, screenLayout, screenOptions, ...rest } = props;
    const { width } = useWindowDimensions();
    const isInSidebar = useIsInSidebar();
    const datastore = useDatastore();
    const headerLarge = width >= LAYOUT_BREAKPOINT_LARGE;

    const stack = (
        <Stack
            {...rest}
            // eslint-disable-next-line react/no-unstable-nested-components
            layout={layout ?? ((props) => <InstanceLayout {...props} />)}
            // eslint-disable-next-line react/no-unstable-nested-components
            screenLayout={screenLayout ?? ((props) => <InstanceScreenLayout {...props} />)}
            screenOptions={(props) => ({
                // eslint-disable-next-line react/no-unstable-nested-components
                header: (props) => <NavigationHeader {...props} isInSidebar={isInSidebar} large={headerLarge} />,
                ...resolveScreenOptions(screenOptions, props),
            })}
        />
    );

    return (
        <>
            {isInSidebar ? (
                <FocusLoopView autoFocus={false} index={0} onCloseLoop={() => closeSidebar(datastore)} style={{ flex: 1 }}>
                    {/* tabIndex={-1} gives clicks on non-focusable content
                        (whitespace, plain text) something to land on besides
                        document.body. Without it, such a click blurs focus to
                        nowhere, and react-aria's FocusScope "restores" focus
                        with a bare .focus() call that scrolls the page back to
                        the previously-focused element, which is jarring if the
                        user had scrolled it out of view. Landing the click here
                        instead keeps focus inside the trap, so that restore
                        never fires. */}
                    <View style={{ flex: 1 }} tabIndex={-1}>
                        <OverlayPortalBoundary>{stack}</OverlayPortalBoundary>
                    </View>
                </FocusLoopView>
            ) : (
                stack
            )}
            <KeyboardShortcutHandler />
        </>
    );
}

/**
 * A navigator layout that renders the instance's bottom-overlay widgets once,
 * over every screen, and starts the sidebar timers in the sidebar.
 */
function InstanceLayout({ children }: { children: React.ReactNode }) {
    const datastore = useDatastore();
    const isInSidebar = useIsInSidebar();

    useEffect(() => {
        if (isInSidebar) {
            initializeGlobalSidebarTimers(datastore);
        }
    }, [datastore, isInSidebar]);

    return (
        <>
            {children}
            <RenderBottomOverlays />
        </>
    );
}

/** A screen layout that handles concerns common to all instance screens. */
export function InstanceScreenLayout({ children }: { children: React.ReactElement }) {
    const [confirmAction, setConfirmAction] = useState<NavigationAction | null>(null);
    const navigation = useNavigation();
    const datastore = useDatastore();
    const screenHasUnsavedChanges = useSessionData('screenHasUnsavedChanges');

    usePreventRemove(screenHasUnsavedChanges, ({ data }) => {
        setConfirmAction(data.action);
    });

    return (
        <NavigationHeaderActionProvider>
            {children}
            {confirmAction && (
                <ConfirmLeaveModal
                    onClose={() => setConfirmAction(null)}
                    onConfirm={() => {
                        datastore.setSessionData('screenHasUnsavedChanges', false);
                        navigation.dispatch(confirmAction);
                        setConfirmAction(null);
                    }}
                />
            )}
        </NavigationHeaderActionProvider>
    );
}

/**
 * Return whether the tab should be visible.
 *
 * Always returns true if the tab has no visibility condition.
 */
function isTabAllowed(tab: DashboardTab, props: IsTabAllowedProps) {
    return tab.allowed ? tab.allowed(props) : true;
}

/**
 * The dashboard tabs this viewer is allowed to see, or `undefined` while the
 * settings the predicates read are still loading.
 *
 * Roles arrive asynchronously, so they are read through `useMyRoles` and the
 * list recomputes when they land.
 */
function useAllowedDashboardTabs(dashboardTabs: DashboardTab[]): DashboardTab[] | undefined {
    const myRoles = useMyRoles();
    const settings = useSettings();

    if (!settings) return undefined;
    return dashboardTabs.filter((tab) => isTabAllowed(tab, { myRoles, settings }));
}

/**
 * Return whether the tab bar should be visible.
 *
 * Always returns true if the tab does not customize tab bar visibility.
 */
function isTabBarVisible(route: RouteProp<ParamListBase>, tab: DashboardTab) {
    const focusedRouteName = getFocusedRouteNameFromRoute(route);
    const screenKey = `${tab.key}/${focusedRouteName}`;
    return tab.tabBarVisible ? tab.tabBarVisible(screenKey) : true;
}

/**
 * Utility function for resolving screen options.
 */
function resolveScreenOptions<ScreenOptions extends object, Navigation>(
    screenOptions:
        | ScreenOptions
        | ((props: { route: RouteProp<ParamListBase>; navigation: Navigation; theme: Theme }) => ScreenOptions)
        | undefined,
    props: { route: RouteProp<ParamListBase>; navigation: Navigation; theme: Theme },
) {
    if (typeof screenOptions === 'function') {
        return screenOptions(props);
    }

    return screenOptions;
}

export type DatastoreProviderProps = InstanceConfig & {
    loadingScreen?: React.JSX.Element;
    children: React.ReactNode;
};

/** Mounts the `Datastore` for a resolved instance config, spread flat. */
export function DatastoreProvider({ loadingScreen, children, ...instanceConfig }: DatastoreProviderProps) {
    const language = useSiloLanguage();
    const appData = useAppData();
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const apiQueryUtils = useApiQueryUtils();

    return (
        <Datastore
            {...instanceConfig}
            apiClient={apiClient}
            apiQueryUtils={apiQueryUtils}
            appData={appData}
            isAdminUi={instanceConfig.structureModule.structure.isAdminUi}
            isTestMode={false}
            language={language}
            loadingScreen={loadingScreen ?? <LoadingSpinnerContainer />}
            queryClient={queryClient}
            testState={null}
        >
            {children}
        </Datastore>
    );
}

function RenderBottomOverlays() {
    const config: any = useConfig();
    const overlays: React.ComponentType[] = config?.bottomOverlayWidgets || [];
    const s = RenderBottomOverlaysStyle;
    if (overlays.length === 0) {
        return null;
    }
    return (
        <View style={s.container}>
            {overlays.map((Overlay, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <Overlay />
                </Catcher>
            ))}
        </View>
    );
}

const RenderBottomOverlaysStyle = StyleSheet.create({
    container: {
        position: 'absolute',
        bottom: spacings.xl,
        right: spacings.xl,
        zIndex: 1000,
    },
});
